import {
  useCallback, useEffect, useRef, type ReactNode,
} from "react";
import { useSelector } from "react-redux";
import { getAccessToken } from "@/features/auth/services/accessToken.service";
import { getAuthDeviceId } from "@/features/auth/services/localStorage.service";
import { AUTO_LOGIN_ROUTE, IMPERSONATE_GRANT_ROUTE, IMPERSONATE_LOGIN_ROUTE } from "@/shared/configs/routes.config";
import { createUsageTracker } from "@/shared/services/usageTracking/usageTracker";
import type { UsageTracker } from "@/shared/services/usageTracking/usageTracking.types";
import type { RootState } from "@/store";
import { UsageTrackingContext } from "./UsageTrackingContext";

// The login page is still displayed when the session starts: it must not count as a page view.
const AUTH_PAGES = new Set([
  "/login", "/mot-de-passe-oublie", "/reconfig", IMPERSONATE_LOGIN_ROUTE, IMPERSONATE_GRANT_ROUTE, AUTO_LOGIN_ROUTE,
]);

// VITE_YEATI_API_URL already ends with /api/admin.
const getApiBaseUrl = (): string | null => {
  try {
    const url = new URL(import.meta.env.VITE_YEATI_API_URL);
    return `${url.origin}${url.pathname.replace(/\/+$/, "")}`;
  }
  catch {
    return null;
  }
};

/**
 * Internal usage measurement (YTI-1855): runs only for an authenticated
 * representative, never during an impersonation session.
 */
const UsageTrackingProvider = ({ children }: { children: ReactNode }) => {
  const userId = useSelector((state: RootState) => {
    const { isAuthenticated, user } = state.session;
    if (!isAuthenticated || !user?.representative || user.representative.impersonation?.active) return null;
    return user.representative.id;
  });
  const trackerRef = useRef<UsageTracker | null>(null);
  const currentPageRef = useRef<string | null>(null);

  useEffect(() => {
    const apiBaseUrl = getApiBaseUrl();
    if (!userId || !apiBaseUrl) return;
    const tracker = createUsageTracker({
      apiBaseUrl,
      visitorToken: getAuthDeviceId(),
      getAccessToken,
      appVersion: import.meta.env.VITE_APP_VERSION || undefined,
    });
    tracker.start();
    // Routes report their page before this effect runs on the first render.
    if (currentPageRef.current) tracker.trackView(currentPageRef.current);
    trackerRef.current = tracker;
    return () => {
      tracker.stop();
      trackerRef.current = null;
    };
  }, [userId]);

  const trackView = useCallback((page: string) => {
    if (AUTH_PAGES.has(page)) {
      currentPageRef.current = null;
      return;
    }
    currentPageRef.current = page;
    trackerRef.current?.trackView(page);
  }, []);

  return <UsageTrackingContext value={trackView}>{children}</UsageTrackingContext>;
};

export default UsageTrackingProvider;
